import React from 'react';
import {observer} from 'mobx-react';
import {icons} from '../../helpers/conversions';
import styled from 'styled-components';
import {colors} from '../../styleVariables';
import './index.sass';
import useFormatter from '../../hooks/user-formatter';
import {wrapHours, wrapOdometer} from '../../helpers/accumulator';
import I18nMessage from '../I18nMessage';
import classNames from 'classnames';
import {useIntl} from 'react-intl';

const DeviceFooterStyle = styled.div`
  margin-top: 5px;
  display: flex;
  padding-left: 1rem;
  padding-right: 1rem;

  * {
    opacity: 1;
  }

  .red * {
    color: ${colors.red1};
  }

  .yellow * {
    color: ${colors.orange1};
  }

  .green * {
    color: ${colors.green2};
  }

  .red * {
    color: ${colors.red};
  }

  .attribute-item[role='button']:focus-visible {
    outline: 2px solid ${colors.green2};
    outline-offset: 2px;
  }

  .fal {
    font-size: 1.3rem;

    + small {
      font-size: 0.8rem !important;
    }
  }

  > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    line-height: 1rem;

    :not(:last-child) {
      border-right: 1px solid ${colors.gray1};
    }

    span {
      color: ${colors.gray1};

      :first-child {
        margin-bottom: 0.1rem;
      }
    }

    small {
      color: ${colors.gray1};
      font-size: 0.8rem !important;
    }
  }
`;

const hasValue = (value) => value !== undefined && value !== null;

const NotAvailableValue = () => (
  <small><strong><I18nMessage id={'notAvailable'} /></strong></small>
);

const Battery = ({value}) => {
  const levels = [
    [70, 'green'],
    [40, 'yellow'],
    [0, 'red'],
  ];

  const icon = hasValue(value) ?
      (icons.find(([k]) => value >= k) || icons[icons.length - 1])[1] :
      'fal fa-battery-empty fa-2x';
  const color = hasValue(value) ? (levels.find(([k]) => value >= k) || levels[levels.length - 1])[1] : '';

  return (
    <div className={`attribute-item battery flex-fill ${color}`}>
      <span className={icon} />
      <small><I18nMessage id={'positionBattery'} /></small>
      {hasValue(value) ?
        <small><strong>{value}%</strong></small> :
        <NotAvailableValue />}
    </div>
  );
};

const Blocked = ({status}) => (
  <div className={`attribute-item blocked flex-fill ${hasValue(status) && !status ? 'green' : ''}`}>
    <span className={`${status ? 'fal fa-lock' : 'fal fa-unlock'} fa-2x`} />
    <small><I18nMessage id={'positionBlocked'} /></small>
    {hasValue(status) ?
      <small>
        <strong>{!status ? <I18nMessage id={'sharedNotActivated'} /> : <I18nMessage id={'sharedActivated'} />}</strong>
      </small> :
      <NotAvailableValue />}
  </div>
);

const Alarm = ({status}) => (
  <div className={`attribute-item panic flex-fill ${!status ? undefined : 'red'}`}>
    <span className={`${status ? 'fal fa-siren-on' : 'fal fa-siren'} fa-2x`} />
    <small><I18nMessage id={'positionAlarm'} /></small>
    <small><strong>{status ? <I18nMessage id={'sharedActivated'} /> : <I18nMessage id={'sharedNotActivated'} />}</strong></small>
  </div>
);

const Ignition = ({status}) => (
  <div className={`attribute-item ignition flex-fill ${status ? 'green' : ''}`}>
    <span className="fal fa-key fa-2x" />
    <small><I18nMessage id={'positionIgnition'} /></small>
    {hasValue(status) ?
      <small>
        <strong>{status ? <I18nMessage id={'deviceStatusOnline'} /> : <I18nMessage id={'deviceStatusOffline'} />}</strong>
      </small> :
      <NotAvailableValue />}
  </div>
);

/**
 * Propriedades de um item que abre a edicao de odometro e horimetro. Sem a funcao o item e so leitura.
 */
const editable = (onEdit, intl) => (onEdit ? {
  role: 'button',
  tabIndex: 0,
  title: intl.formatMessage({id: 'accumulatorEditHint'}),
  style: {cursor: 'pointer'},
  onClick: onEdit,
  onKeyDown: (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onEdit();
    }
  },
} : {});

const Odometer = ({value, onEdit}) => {
  const intl = useIntl();
  return (
    <div className={`attribute-item odometer flex-fill ${hasValue(value) ? 'green' : ''}`} {...editable(onEdit, intl)}>
      <span className="fal fa-digital-tachograph fa-2x" />
      <small><I18nMessage id={'positionOdometer'} /></small>
      {hasValue(value) ? <small><strong>{value}</strong></small> : <NotAvailableValue />}
    </div>
  );
};

const Runtime = ({value, onEdit}) => {
  const intl = useIntl();
  return (
    <div className={`attribute-item runtime flex-fill ${hasValue(value) ? 'green' : ''}`} {...editable(onEdit, intl)}>
      <span className="fal fa-clock fa-2x" />
      <small><I18nMessage id={'positionHours'} /></small>
      {hasValue(value) ? <small><strong>{value}</strong></small> : <NotAvailableValue />}
    </div>
  );
};


const Power = ({value}) => (
  <div className={`attribute-item network flex-fill ${hasValue(value) ? 'green' : ''}`}>
    <span className="fal fa-car-battery fa-2x" />
    <small><I18nMessage id={'positionPower'} /></small>
    {hasValue(value) ? <small><strong>{value}</strong></small> : <NotAvailableValue />}
  </div>
);


const DeviceInfoAttributes = (props) => {
  const {formatValue} = useFormatter();
  const {currentPosition, theme, className, onEditAccumulators} = props;
  const attributes =
        currentPosition && currentPosition.attributes ?
            currentPosition.attributes :
            {};

  const {
    batteryLevel,
    battery,
    blocked,
    ignition,
    alarm,
    odometer,
    displayOdometer,
    runtime,
    hours,
    power,
  } = attributes;

  const format = (key, value) => (hasValue(value) ? formatValue(key, value) : undefined);

  // O servidor decide o que o card mostra em displayOdometer (odometro do dispositivo, o ultimo recebido, ou o da
  // plataforma). Posicao gravada antes disso nao o tem, e o card usa o odometro do pacote. Como um painel de veiculo,
  // o contador reinicia em zero depois do maximo.
  const odometerValue = hasValue(displayOdometer) ? displayOdometer : odometer;
  const hoursValue = hasValue(runtime) ? runtime : hours;
  const powerValue = hasValue(power) ? power : battery;

  return (
    <DeviceFooterStyle theme={theme} className={classNames('row', className)}>
      <Ignition status={ignition} />
      <Blocked status={blocked} />
      <Battery value={batteryLevel} />
      <Odometer
        value={format('odometer', hasValue(odometerValue) ? wrapOdometer(odometerValue) : undefined)}
        onEdit={onEditAccumulators} />
      <Runtime
        value={format('hours', hasValue(hoursValue) ? wrapHours(hoursValue) : undefined)}
        onEdit={onEditAccumulators} />
      <Power value={format('power', powerValue)} />
      {hasValue(alarm) && <Alarm status={alarm} />}
    </DeviceFooterStyle>
  );
};

export default observer(DeviceInfoAttributes);
